import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toggleReportColumnSelection } from '@/api/reportTableColumns';

/** Keeps prior order for known ids; appends any new default ids at the end. */
export function reconcileColumnOrder(prev: string[], defaultIds: string[]): string[] {
  const allowed = new Set(defaultIds);
  const kept = prev.filter((id) => allowed.has(id));
  const keptSet = new Set(kept);
  const appended = defaultIds.filter((id) => !keptSet.has(id));
  return [...kept, ...appended];
}

/**
 * Drops unknown ids. Does not re-show columns the user hid.
 * When `previousDefaultIds` is provided, only ids newly added to the schema
 * are appended as visible.
 */
export function reconcileColumnVisibility(
  prev: string[],
  defaultIds: string[],
  previousDefaultIds: readonly string[] = defaultIds
): string[] {
  const allowed = new Set(defaultIds);
  const kept = prev.filter((id) => allowed.has(id));
  const previousDefaults = new Set(previousDefaultIds);
  const keptSet = new Set(kept);
  const appended = defaultIds.filter((id) => !previousDefaults.has(id) && !keptSet.has(id));
  return [...kept, ...appended];
}

export function visibleColumnOrder(order: string[], visibleIds: string[]): string[] {
  const visible = new Set(visibleIds);
  return order.filter((id) => visible.has(id));
}

const EMPTY_LEADING_FREEZE_IDS: readonly string[] = [];

/** Leading entity column id used by metric-only Workspace tables (Type, Match Type, …). */
export const WORKSPACE_LABEL_LEADING_FREEZE_IDS: readonly string[] = ['label'];

/** Visual order of freezeable columns: leading label ids, then visible data columns. */
export function freezeableColumnOrder(
  visibleOrder: string[],
  leadingFreezeIds: readonly string[] = EMPTY_LEADING_FREEZE_IDS
): string[] {
  const leading = leadingFreezeIds.filter(Boolean);
  const leadingSet = new Set(leading);
  return [...leading, ...visibleOrder.filter((id) => !leadingSet.has(id))];
}

/**
 * First actual data column: leading label (Type, Match Type, …) when present,
 * otherwise the first visible data column. The checkbox is never freezeable.
 */
export function pinnedFrozenColumnId(
  visibleOrder: readonly string[],
  leadingFreezeIds: readonly string[] = EMPTY_LEADING_FREEZE_IDS
): string | undefined {
  return freezeableColumnOrder([...visibleOrder], leadingFreezeIds)[0];
}

/**
 * Default freeze is the first freezeable data column (leading label, else first
 * visible). The checkbox/selection column is not freezeable and is ignored.
 */
export function defaultFrozenColumnIds(
  visibleOrder: string[],
  leadingFreezeIds: readonly string[] = EMPTY_LEADING_FREEZE_IDS
): string[] {
  const first = pinnedFrozenColumnId(visibleOrder, leadingFreezeIds);
  return first ? [first] : [];
}

/**
 * Keeps currently frozen ids that are still freezeable, in freeze-append order.
 * The first data column is always frozen and listed first so sticky offsets stay
 * aligned. Leading freeze ids (e.g. Type/label) stay first when they are that
 * pinned column. Additional frozen ids follow in freeze-append order.
 */
export function resolveFrozenColumnIds(
  frozenIds: readonly string[] | null | undefined,
  visibleOrder: string[],
  leadingFreezeIds: readonly string[] = EMPTY_LEADING_FREEZE_IDS,
  pinnedColumnId?: string | null
): string[] {
  const freezeableOrder = freezeableColumnOrder(visibleOrder, leadingFreezeIds);
  const freezeable = new Set(freezeableOrder);
  const pinned =
    pinnedColumnId && freezeable.has(pinnedColumnId) ? pinnedColumnId : freezeableOrder[0];
  if (!pinned) return [];

  const kept = (frozenIds ?? []).filter((id) => freezeable.has(id) && id !== pinned);
  const leadingSet = new Set(leadingFreezeIds.filter(Boolean));
  const leadingFrozen = kept.filter((id) => leadingSet.has(id));
  const rest = kept.filter((id) => !leadingSet.has(id));
  return [pinned, ...leadingFrozen, ...rest];
}

/**
 * Visual table order: frozen columns grouped at the left (freeze-append order),
 * then remaining visible columns in their original positions. Does not mutate
 * the underlying visible/data order.
 */
export function visualColumnOrder(
  visibleOrder: readonly string[],
  frozenColumnIds: readonly string[]
): string[] {
  if (!frozenColumnIds.length) return [...visibleOrder];
  const visible = new Set(visibleOrder);
  const frozenVisible = frozenColumnIds.filter((id) => visible.has(id));
  if (!frozenVisible.length) return [...visibleOrder];
  const frozenSet = new Set(frozenVisible);
  return [...frozenVisible, ...visibleOrder.filter((id) => !frozenSet.has(id))];
}

export function toggleFrozenColumnIds(
  frozenIds: readonly string[],
  columnId: string,
  pinnedColumnId?: string | null
): string[] {
  if (pinnedColumnId && columnId === pinnedColumnId) {
    return frozenIds.includes(pinnedColumnId) ? [...frozenIds] : [...frozenIds, pinnedColumnId];
  }
  return frozenIds.includes(columnId)
    ? frozenIds.filter((id) => id !== columnId)
    : [...frozenIds, columnId];
}

export function moveColumnLeft(order: string[], id: string): string[] {
  const idx = order.indexOf(id);
  if (idx <= 0) return order;
  const next = [...order];
  [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
  return next;
}

export function moveColumnRight(order: string[], id: string): string[] {
  const idx = order.indexOf(id);
  if (idx < 0 || idx >= order.length - 1) return order;
  const next = [...order];
  [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
  return next;
}

export function moveColumnBefore(order: string[], draggedId: string, targetId: string): string[] {
  if (draggedId === targetId) return order;
  const from = order.indexOf(draggedId);
  const to = order.indexOf(targetId);
  if (from < 0 || to < 0) return order;

  const next = order.filter((id) => id !== draggedId);
  const nextTargetIndex = next.indexOf(targetId);
  if (nextTargetIndex < 0) return order;

  next.splice(nextTargetIndex, 0, draggedId);
  return next;
}

export function moveColumnAfter(order: string[], draggedId: string, targetId: string): string[] {
  if (draggedId === targetId) return order;
  const from = order.indexOf(draggedId);
  const to = order.indexOf(targetId);
  if (from < 0 || to < 0) return order;

  const next = order.filter((id) => id !== draggedId);
  const nextTargetIndex = next.indexOf(targetId);
  if (nextTargetIndex < 0) return order;

  next.splice(nextTargetIndex + 1, 0, draggedId);
  return next;
}

export type ColumnDropPosition = 'before' | 'after';

export function useColumnOrder(
  defaultIds: string[],
  initialVisibleIds?: string[],
  leadingFreezeIds: readonly string[] = EMPTY_LEADING_FREEZE_IDS,
  defaultFrozenIds?: readonly string[]
) {
  const defaultKey = defaultIds.join('\0');
  const [order, setOrder] = useState<string[]>(() => [...defaultIds]);
  const [frozenColumnIds, setFrozenColumnIds] = useState<string[]>(() => {
    const visible = visibleColumnOrder(defaultIds, initialVisibleIds ?? defaultIds);
    if (defaultFrozenIds && defaultFrozenIds.length > 0) {
      return resolveFrozenColumnIds(defaultFrozenIds, visible, leadingFreezeIds);
    }
    return defaultFrozenColumnIds(visible, leadingFreezeIds);
  });
  const [visibleIds, setVisibleIdsState] = useState<string[]>(() =>
    initialVisibleIds ? [...initialVisibleIds] : [...defaultIds]
  );
  const [activeDragColumnId, setActiveDragColumnId] = useState<string | null>(null);
  const [hoverColumnId, setHoverColumnId] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<ColumnDropPosition | null>(null);
  const [hoverInsertIndex, setHoverInsertIndex] = useState<number | null>(null);
  const previousDefaultIdsRef = useRef(defaultIds);
  const previousDefaultKeyRef = useRef(defaultKey);

  useEffect(() => {
    if (previousDefaultKeyRef.current === defaultKey) return;
    const previousDefaultIds = previousDefaultIdsRef.current;
    previousDefaultKeyRef.current = defaultKey;
    previousDefaultIdsRef.current = defaultIds;
    setVisibleIdsState((prev) => reconcileColumnVisibility(prev, defaultIds, previousDefaultIds));
  }, [defaultKey, defaultIds]);

  const displayedOrder = useMemo(
    () => reconcileColumnOrder(order, defaultIds),
    // defaultKey captures defaultIds contents
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order, defaultKey]
  );

  const displayedVisibleIds = useMemo(
    () => reconcileColumnVisibility(visibleIds, defaultIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleIds, defaultKey]
  );

  const visibleOrder = useMemo(
    () => visibleColumnOrder(displayedOrder, displayedVisibleIds),
    [displayedOrder, displayedVisibleIds]
  );

  const setVisibleIds = useCallback(
    (next: string[]) => {
      // Pass defaultIds as previousDefaultIds so intentionally omitted ids stay hidden.
      setVisibleIdsState(reconcileColumnVisibility(next, defaultIds, defaultIds));
    },
    [defaultIds]
  );

  const toggleVisible = useCallback(
    (columnId: string, checked: boolean, requiredColumnId?: string | readonly string[]) => {
      setVisibleIdsState((prev) =>
        toggleReportColumnSelection(
          reconcileColumnVisibility(prev, defaultIds),
          columnId,
          checked,
          requiredColumnId
        )
      );
    },
    [defaultIds]
  );

  const pinnedColumnId = useMemo(
    () =>
      pinnedFrozenColumnId(
        visibleColumnOrder(defaultIds, displayedVisibleIds),
        leadingFreezeIds
      ) ?? null,
    // defaultKey captures defaultIds contents
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultKey, displayedVisibleIds, leadingFreezeIds]
  );

  const resolvedFrozenColumnIds = useMemo(
    () => resolveFrozenColumnIds(frozenColumnIds, visibleOrder, leadingFreezeIds, pinnedColumnId),
    [frozenColumnIds, visibleOrder, leadingFreezeIds, pinnedColumnId]
  );

  const displayOrder = useMemo(
    () => visualColumnOrder(visibleOrder, resolvedFrozenColumnIds),
    [visibleOrder, resolvedFrozenColumnIds]
  );

  const freezeSelectedColumn = useCallback(
    (id: string) => {
      if (pinnedColumnId && id === pinnedColumnId) return;
      setFrozenColumnIds((prev) => toggleFrozenColumnIds(prev, id, pinnedColumnId));
    },
    [pinnedColumnId]
  );

  const moveLeft = useCallback(
    (id: string) => {
      setOrder((prev) => moveColumnLeft(reconcileColumnOrder(prev, defaultIds), id));
    },
    [defaultIds]
  );

  const moveRight = useCallback(
    (id: string) => {
      setOrder((prev) => moveColumnRight(reconcileColumnOrder(prev, defaultIds), id));
    },
    [defaultIds]
  );

  const moveBefore = useCallback(
    (draggedId: string, targetId: string) => {
      setOrder((prev) =>
        moveColumnBefore(reconcileColumnOrder(prev, defaultIds), draggedId, targetId)
      );
    },
    [defaultIds]
  );

  const moveAfter = useCallback(
    (draggedId: string, targetId: string) => {
      setOrder((prev) =>
        moveColumnAfter(reconcileColumnOrder(prev, defaultIds), draggedId, targetId)
      );
    },
    [defaultIds]
  );

  const startDrag = useCallback((columnId: string) => {
    setActiveDragColumnId(columnId);
    setHoverColumnId(null);
    setHoverPosition(null);
    setHoverInsertIndex(null);
  }, []);

  const hoverReorder = useCallback(
    (
      targetId: string,
      position: ColumnDropPosition,
      _clientX?: number,
      insertIndex?: number
    ) => {
      const skipped = !activeDragColumnId || activeDragColumnId === targetId;
      if (skipped) return;

      if (typeof insertIndex === 'number') {
        setHoverInsertIndex((curr) => (curr === insertIndex ? curr : insertIndex));
      }

      setHoverColumnId((curr) => (curr === targetId ? curr : targetId));
      setHoverPosition((curr) => (curr === position ? curr : position));
    },
    [activeDragColumnId]
  );

  const commitDrop = useCallback(
    (targetId: string, position: ColumnDropPosition) => {
      const draggedId = activeDragColumnId;
      if (!draggedId || draggedId === targetId) return;
      setOrder((prev) => {
        const base = reconcileColumnOrder(prev, defaultIds);
        return position === 'before'
          ? moveColumnBefore(base, draggedId, targetId)
          : moveColumnAfter(base, draggedId, targetId);
      });
      setHoverColumnId(targetId);
      setHoverPosition(position);
    },
    [activeDragColumnId, defaultIds]
  );

  const endDrag = useCallback(() => {
    setActiveDragColumnId(null);
    setHoverColumnId(null);
    setHoverPosition(null);
    setHoverInsertIndex(null);
  }, []);

  const canMoveLeft = useCallback(
    (id: string) => displayedOrder.indexOf(id) > 0,
    [displayedOrder]
  );

  const canMoveRight = useCallback(
    (id: string) => {
      const idx = displayedOrder.indexOf(id);
      return idx >= 0 && idx < displayedOrder.length - 1;
    },
    [displayedOrder]
  );

  return {
    order: displayedOrder,
    visibleOrder,
    displayOrder,
    visibleIds: displayedVisibleIds,
    setVisibleIds,
    toggleVisible,
    frozenColumnIds: resolvedFrozenColumnIds,
    pinnedFrozenColumnId: pinnedColumnId,
    freezeColumn: freezeSelectedColumn,
    moveLeft,
    moveRight,
    moveBefore,
    moveAfter,
    activeDragColumnId,
    hoverColumnId,
    hoverPosition,
    hoverInsertIndex,
    startDrag,
    hoverReorder,
    commitDrop,
    endDrag,
    canMoveLeft,
    canMoveRight,
  };
}
